import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useFormikContext} from "formik";
import {t} from "i18next";

import useColumns from "../../../hook/useColumns";
import {getCurrentUser, getSuccess, getUsers} from "../../../slices/UserSlice";
import {dateFormat} from "../../../utilities/dateFormat";
import {swalCustom} from "../../../utilities/swalCustom";
import {useFiltersContext} from "../../conteiners/filters/useSessionFiltersContext";
// import translit from "ua-en-translit";
import {
  addNewTicketFile,
  addNewTicketTask,
  clearCurrentTicket,
  deleteTicketFile,
  deleteTicketTask,
  fetchAllTickets,
  fetchAllTicketTypes,
  fetchDevITFilters,
  fetchTicket,
  selectAllDevITTickets,
  selectDevITLoading,
  selectDevITTicket,
  updateTicket,
  updateTicketTask,
} from "../appealModal/DevITSlice";
import {expandedTextArray, makeTableNameFromPersona} from "../appealModal/useDevITBody";
import {IT_MANAGERS_IDS} from "../DevITButton";

import {MANAGER_IT_TABLE_COLUMNS, PROJECT_TASKS_LIST_TABLE_COLUMNS} from "./constants";

const useDevTicketsList = () => {
  const dispatch = useDispatch();
  const {
    i18n: {language},
  } = useTranslation();
  const {initialValues, values, setValues} = useFormikContext();
  const {itNote, itManagerId, planDate, isDone} = values;
  const {filters} = useFiltersContext();

  const allTickets = useSelector(selectAllDevITTickets);
  const currentTicket = useSelector(selectDevITTicket);
  const isCurrentTicketInWork = currentTicket?.status?.statusId === 2;
  const isCurrentTicketClosed = currentTicket?.status?.statusId === 3;
  const loading = useSelector(selectDevITLoading);
  const users = useSelector(getUsers);
  const user = useSelector(getCurrentUser);

  const appealTypes = [];
  const fileArray = currentTicket?.files?.map(({fileUrl}) => fileUrl) ?? [];
  const doneItemsIds = allTickets?.filter(ticket => ticket.status.statusId === 3)?.map(ticket => ticket.ticketId);

  const [currentTicketId, setCurrentTicketId] = useState(null);

  useEffect(() => {
    dispatch(clearCurrentTicket());
  }, []);

  useEffect(() => {
    if (currentTicketId) {
      dispatch(fetchTicket({ticketId: currentTicketId}));
    }
  }, [currentTicketId]);

  useEffect(() => {
    if (currentTicket?.ticketId) {
      setValues(prev => ({
        ...prev,
        ticketText: currentTicket?.text,
        itNote: currentTicket?.note,
        // itManagerId: "",
        // planDate: "",
        // isDone: false,
      }));
    } else {
      setCurrentTicketId(null);
      setValues(initialValues);
    }
  }, [currentTicket?.ticketId]);

  const onFilterChange = filters => {
    dispatch(clearCurrentTicket());

    if (!filters) {
      dispatch(fetchAllTickets({}));
      return;
    }

    dispatch(fetchAllTickets({params: filters}));
  };

  useEffect(() => {
    dispatch(fetchDevITFilters({params: {}})).then(() => {
      onFilterChange(filters);
    });
  }, []);

  const [currentTaskId, setCurrentTaskId] = useState(null);
  const currentTask = currentTicket?.tasks?.find(({taskId}) => currentTaskId === taskId);

  useEffect(() => {
    if (currentTaskId) {
      setValues(prev => ({
        ...prev,
        itManagerId: currentTask?.performer?.userId,
        planDate: currentTask?.deadline,
        isDone: Boolean(currentTask?.finishDate),
      }));
    } else {
      setValues(prev => ({
        ...prev,
        itManagerId: "",
        planDate: "",
        isDone: "",
      }));
    }
  }, [currentTaskId]);

  useEffect(() => {
    if (currentTicket?.tasks?.length > 0) {
      setCurrentTaskId(currentTicket?.tasks[0]?.taskId);
    } else setCurrentTaskId(null);
  }, [currentTicket]);

  const iTManagersOptions = useMemo(
    () =>
      users
        ?.filter?.(user => IT_MANAGERS_IDS.includes(user.userId))
        ?.map(({userId, firstName, lastName}) => ({value: userId, title: `${lastName} ${firstName}`})) ?? [],

    [users],
  );

  const ticketsTableColumns = useColumns(PROJECT_TASKS_LIST_TABLE_COLUMNS);
  const ticketsTableData =
    allTickets?.map(
      (
        {
          ticketId,
          type: {nameEng: typeNameEng, nameUkr: typeNameUkr},
          block: {nameEng: blockNameEng, nameUkr: blockNameUkr},
          status: {nameEng: statusNameEng, nameUkr: statusNameUkr},
          creator: {firstName, lastName},
          text,
          note,
          startDate,
          // deadline,
          finishDate,
          createdAt,
          tasks,
        },
        index,
      ) => ({
        number: ++index,
        id: ticketId,
        date: dateFormat(createdAt),
        appealType: language === "en" ? typeNameEng : typeNameUkr,
        about: text,
        block: language === "en" ? blockNameEng : blockNameUkr,
        manager: lastName + " " + firstName,
        status: language === "en" ? statusNameEng : statusNameUkr,
        managerIT: tasks?.[0] ? tasks?.[0]?.performer?.lastName + " " + tasks?.[0]?.performer?.firstName : " ",
        startDate: dateFormat(startDate) ?? "-",
        planDate: tasks?.[0] ? dateFormat(tasks?.[0]?.deadline) ?? "-" : "-",
        finishDate: dateFormat(finishDate) ?? "-",
        isDone: Boolean(finishDate),
        noteIT: note ?? "-",
      }),
    ) ?? [];

  const onTicketsTableSelect = ticketId => {
    setCurrentTicketId(ticketId);
    setCurrentTaskId(null);

    if (!ticketId) {
      dispatch(clearCurrentTicket());
    }
  };

  const managerITTableColumns = useColumns(MANAGER_IT_TABLE_COLUMNS);
  const managerITTableData =
    currentTicket?.tasks?.map(({taskId, performer: {persona}, finishDate, deadline}, index) => ({
      number: ++index,
      id: taskId,
      managerIT: makeTableNameFromPersona({persona, language}),
      planDate: dateFormat(deadline),
      finishDate: dateFormat(finishDate),
      isDone: finishDate ? t("done") : "",
    })) ?? [];

  const onManagerITTableSelect = taskId => {
    setCurrentTaskId(taskId);
  };

  const getTommorowDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return tomorrow;
  };

  const onTakeIntoWorkBtnClick = () => {
    if (!currentTicketId) return;
    dispatch(updateTicket({ticketId: currentTicketId, body: {statusId: 2}})).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        if (currentTicket?.tasks?.length < 1)
          dispatch(
            addNewTicketTask({
              ticketId: currentTicketId,
              body: {userId: user.userId, deadline: planDate || getTommorowDate()},
            }),
          ).then(resp => {
            if (resp.meta.requestStatus === "fulfilled") {
              getSuccess("Ticket was taken into work");
              setCurrentTaskId(resp.payload.tasks[0].taskId);
            }
          });
      }
    });
  };

  const onCancelTakeIntoWorkBtnClick = () => {
    if (!currentTicketId) return;
    dispatch(
      deleteTicketTask({
        taskId: currentTaskId,
      }),
    ).then(resp => {
      setCurrentTaskId(null);
      if (resp.meta.requestStatus === "fulfilled") {
        dispatch(updateTicket({ticketId: currentTicketId, body: {statusId: 1}})).then(resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            getSuccess("Ticket was back to new status");
          }
        });
      }
    });
  };

  const hasTaskDataChanged =
    (currentTask?.performer?.userId !== itManagerId ||
      currentTask?.deadline !== planDate ||
      Boolean(currentTask?.finishDate) !== isDone) &&
    itManagerId &&
    planDate;
  const hasItNoteChanged = currentTicket?.note !== itNote && itNote;

  const onSaveBtnClick = () => {
    let toastText = null;
    if (hasItNoteChanged) {
      dispatch(updateTicket({ticketId: currentTicketId, body: {note: itNote}})).then(resp => {
        if (resp.meta.requestStatus === "fulfilled") {
          toastText = "Ticket updated";
        }
      });
    }
    if (currentTaskId && (currentTask?.performer?.userId !== itManagerId || currentTask?.deadline !== planDate)) {
      dispatch(updateTicketTask({taskId: currentTaskId, body: {userId: itManagerId, deadline: planDate}})).then(
        resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            toastText = "Ticket updated";
          }
        },
      );
    }
    if (toastText) getSuccess(toastText);
  };

  const onReadyBtnClick = () => {
    if (!currentTicketId) return;

    swalCustom.confirm({
      confirmFunc: () => {
        dispatch(updateTicketTask({taskId: currentTaskId, body: {isDone: true}})).then(resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            dispatch(updateTicket({ticketId: currentTicketId, body: {statusId: isCurrentTicketClosed ? 2 : 3}})).then(
              resp => {
                if (resp.meta.requestStatus === "fulfilled") {
                  getSuccess(isCurrentTicketClosed ? "Ticket returned to work" : "Ticket is ready");
                  dispatch(fetchTicket({ticketId: currentTicketId}));
                  dispatch(fetchAllTickets({params: filters}));
                }
              },
            );
          }
        });
      },
    });
  };

  const files = currentTicket?.files ?? [];

  const onAddTicketFile = arrFiles => {
    if (!currentTicketId) return;
    const formdata = new FormData();
    let file = arrFiles[0];
    let filename = file.name;
    formdata.append("file", file, filename);

    if (formdata.has("file")) {
      dispatch(addNewTicketFile({ticketId: currentTicketId, body: formdata})).then(resp => {
        if (resp.meta.requestStatus === "fulfilled") {
          getSuccess("Ticket file added");
        }
      });
    }
  };

  const onDeleteTicketFile = fileId => {
    if (!currentTicketId) return;
    dispatch(deleteTicketFile({fileId})).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        getSuccess("Ticket file deleted");
      }
    });
  };

  return {
    appealTypes,
    fileArray,
    ticketsTableColumns,
    ticketsTableData,
    onTakeIntoWorkBtnClick,
    onSaveBtnClick,
    onReadyBtnClick,
    iTManagersOptions,
    managerITTableColumns,
    managerITTableData,
    onTicketsTableSelect,
    currentTicketId,
    loading,
    isCurrentTicketInWork,
    isCurrentTicketClosed,
    doneItemsIds,
    onManagerITTableSelect,
    currentTaskId,
    files,
    onAddTicketFile,
    onDeleteTicketFile,
    hasItNoteChanged,
    hasTaskDataChanged,
    onCancelTakeIntoWorkBtnClick,
    currentTicket,
  };
};

export default useDevTicketsList;
